/**
 * FikoWebGPU - Native WebGPU Compute Pipeline
 *
 * WebGL2 Transform Feedback'ten WebGPU'ya gecis:
 * - Native compute shaders (vs render-to-texture hack)
 * - Explicit async (vs implicit GPU stall)
 * - Buffer-based memory (vs texture-based)
 * - Better debugging & error handling
 *
 * Fallback: WebGL2 destegi korunur
 */

/**
 * Get GPU adapter options, excluding powerPreference on Windows
 * (Chrome bug: https://crbug.com/369219127)
 */
export function getGPUAdapterOptions(): GPURequestAdapterOptions {
    const isWindows = navigator.platform?.toLowerCase().includes('win') ||
                      navigator.userAgent?.toLowerCase().includes('windows');
    // Windows'ta powerPreference ignored, uyarı veriyor - atla
    return isWindows ? {} : { powerPreference: 'high-performance' };
}

export interface WebGPUComputeResult {
    data: Float32Array;
    gpuTime: number;
}

export interface WebGPUBufferDescriptor {
    size: number;
    usage: number; // GPUBufferUsage flags
    mappedAtCreation?: boolean;
}

export interface WebGPUComputePipelineDescriptor {
    shader: string;
    entryPoint?: string;
    bindGroupLayout?: GPUBindGroupLayoutDescriptor;
    workgroupSize?: [number, number?, number?];
}

export class FikoWebGPU {
    private static instance: FikoWebGPU | null = null;

    private adapter: GPUAdapter | null = null;
    private device: GPUDevice | null = null;
    private queue: GPUQueue | null = null;

    private isAvailable: boolean = false;
    private initPromise: Promise<boolean> | null = null;

    // Pipeline cache
    private pipelineCache: Map<string, GPUComputePipeline> = new Map();
    private shaderModuleCache: Map<string, GPUShaderModule> = new Map();

    // Buffer pool for reuse
    private bufferPool: Map<number, GPUBuffer[]> = new Map();

    // Stats
    private stats = {
        computeDispatches: 0,
        totalGPUTime: 0,
        bufferPoolHits: 0,
        bufferPoolMisses: 0,
        pipelineCacheHits: 0,
        pipelineCacheMisses: 0
    };

    private constructor() {}

    // Singleton pattern
    static getInstance(): FikoWebGPU {
        if (!FikoWebGPU.instance) {
            FikoWebGPU.instance = new FikoWebGPU();
        }
        return FikoWebGPU.instance;
    }

    // Convenience static method
    static async initialize(): Promise<FikoWebGPU> {
        const instance = FikoWebGPU.getInstance();
        await instance.init();
        return instance;
    }

    // ============================================================================
    // INITIALIZATION
    // ============================================================================

    async init(): Promise<boolean> {
        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = this._doInit();
        return this.initPromise;
    }

    private async _doInit(): Promise<boolean> {
        // Check WebGPU support
        if (!navigator.gpu) {
            this.isAvailable = false;
            return false;
        }

        try {
            // Request adapter (skip powerPreference on Windows - Chrome bug)
            this.adapter = await navigator.gpu.requestAdapter(getGPUAdapterOptions());

            if (!this.adapter) {
                this.isAvailable = false;
                return false;
            }

            // Request device with features
            this.device = await this.adapter.requestDevice({
                requiredFeatures: [],
                requiredLimits: {
                    maxStorageBufferBindingSize: 1024 * 1024 * 256, // 256MB
                    maxComputeWorkgroupStorageSize: 16384,
                    maxComputeInvocationsPerWorkgroup: 256
                }
            });

            if (!this.device) {
                this.isAvailable = false;
                return false;
            }

            this.queue = this.device.queue;
            this.isAvailable = true;

            if ('lost' in this.device) {
                (this.device as any).lost.then((info: any) => {
                    console.error('[FikoWebGPU] Device lost:', info?.message || 'unknown');
                    this.isAvailable = false;
                    this.device = null;
                    this.adapter = null;
                }).catch((err: any) => {
                    console.error('[FikoWebGPU] device.lost promise rejected:', err);
                    this.isAvailable = false;
                    this.device = null;
                    this.adapter = null;
                });
            }

            return true;

        } catch (error) {
            this.isAvailable = false;
            return false;
        }
    }

    // ============================================================================
    // BUFFER MANAGEMENT
    // ============================================================================

    createBuffer(descriptor: WebGPUBufferDescriptor): GPUBuffer | null {
        if (!this.device) return null;

        // Try to get from pool
        const poolKey = descriptor.size;
        const pool = this.bufferPool.get(poolKey);

        if (pool && pool.length > 0) {
            this.stats.bufferPoolHits++;
            const buffer = pool.pop()!;
            // Reset buffer if needed
            return buffer;
        }

        this.stats.bufferPoolMisses++;

        return this.device.createBuffer({
            size: descriptor.size,
            usage: descriptor.usage,
            mappedAtCreation: descriptor.mappedAtCreation
        } as GPUBufferDescriptor);
    }

    createStorageBuffer(data: Float32Array, _label?: string): GPUBuffer | null {
        if (!this.device) return null;

        const buffer = this.device.createBuffer({
            size: data.byteLength,
            usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST,
            mappedAtCreation: true
        } as GPUBufferDescriptor);

        new Float32Array(buffer.getMappedRange()).set(data);
        buffer.unmap();

        return buffer;
    }

    createReadbackBuffer(size: number, _label?: string): GPUBuffer | null {
        if (!this.device) return null;

        return this.device.createBuffer({
            size,
            usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ
        } as GPUBufferDescriptor);
    }

    releaseBuffer(buffer: GPUBuffer, poolSize?: number): void {
        if (poolSize) {
            let pool = this.bufferPool.get(poolSize);
            if (!pool) {
                pool = [];
                this.bufferPool.set(poolSize, pool);
            }
            if (pool.length < 10) { // Max 10 buffers per size
                pool.push(buffer);
                return;
            }
        }
        buffer.destroy();
    }

    // ============================================================================
    // SHADER & PIPELINE
    // ============================================================================

    createShaderModule(code: string, _label?: string): GPUShaderModule | null {
        if (!this.device) return null;

        // Check cache
        const cacheKey = code;
        if (this.shaderModuleCache.has(cacheKey)) {
            return this.shaderModuleCache.get(cacheKey)!;
        }

        const module = this.device.createShaderModule({
            code
        } as GPUShaderModuleDescriptor);

        this.shaderModuleCache.set(cacheKey, module);
        return module;
    }

    createComputePipeline(descriptor: WebGPUComputePipelineDescriptor): GPUComputePipeline | null {
        if (!this.device) return null;

        // Check cache
        const cacheKey = descriptor.shader + (descriptor.entryPoint || 'main');
        if (this.pipelineCache.has(cacheKey)) {
            this.stats.pipelineCacheHits++;
            return this.pipelineCache.get(cacheKey)!;
        }

        this.stats.pipelineCacheMisses++;

        const shaderModule = this.createShaderModule(descriptor.shader);
        if (!shaderModule) return null;

        const pipeline = this.device.createComputePipeline({
            layout: 'auto',
            compute: {
                module: shaderModule,
                entryPoint: descriptor.entryPoint || 'main'
            }
        });

        this.pipelineCache.set(cacheKey, pipeline);
        return pipeline;
    }

    // ============================================================================
    // COMPUTE DISPATCH
    // ============================================================================

    async dispatch(
        pipeline: GPUComputePipeline,
        bindGroup: GPUBindGroup,
        workgroupCount: [number, number?, number?],
        resultBuffer?: GPUBuffer,
        resultSize?: number
    ): Promise<WebGPUComputeResult | null> {
        if (!this.device || !this.queue) return null;

        const startTime = performance.now();

        // Create command encoder
        const commandEncoder = this.device.createCommandEncoder();

        // Compute pass
        const computePass = commandEncoder.beginComputePass();

        computePass.setPipeline(pipeline);
        computePass.setBindGroup(0, bindGroup);
        computePass.dispatchWorkgroups(
            workgroupCount[0],
            workgroupCount[1] || 1,
            workgroupCount[2] || 1
        );
        computePass.end();

        // Copy result if needed
        let readbackBuffer: GPUBuffer | null = null;
        if (resultBuffer && resultSize) {
            readbackBuffer = this.createReadbackBuffer(resultSize, 'result-readback');
            if (readbackBuffer) {
                commandEncoder.copyBufferToBuffer(
                    resultBuffer, 0,
                    readbackBuffer, 0,
                    resultSize
                );
            }
        }

        // Submit
        this.queue.submit([commandEncoder.finish()]);

        // Readback if needed
        let resultData: Float32Array | null = null;
        if (readbackBuffer) {
            await readbackBuffer.mapAsync(GPUMapMode.READ);
            const mappedRange = readbackBuffer.getMappedRange();
            resultData = new Float32Array(mappedRange.slice(0));
            readbackBuffer.unmap();
            readbackBuffer.destroy();
        }

        const gpuTime = performance.now() - startTime;
        this.stats.computeDispatches++;
        this.stats.totalGPUTime += gpuTime;

        return {
            data: resultData || new Float32Array(0),
            gpuTime
        };
    }

    // ============================================================================
    // HIGH-LEVEL COMPUTE HELPERS
    // ============================================================================

    async computeWithData(
        shaderCode: string,
        inputData: Float32Array,
        outputSize: number,
        uniforms?: Float32Array
    ): Promise<WebGPUComputeResult | null> {
        if (!this.device) return null;

        // Create pipeline
        const pipeline = this.createComputePipeline({
            shader: shaderCode,
            entryPoint: 'main'
        });
        if (!pipeline) return null;

        // Create buffers
        const inputBuffer = this.createStorageBuffer(inputData, 'input');
        if (!inputBuffer) return null;

        const outputBuffer = this.device.createBuffer({
            size: outputSize * 4, // Float32
            usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC
        } as GPUBufferDescriptor);

        // Create bind group
        const entries: GPUBindGroupEntry[] = [
            { binding: 0, resource: { buffer: inputBuffer } },
            { binding: 1, resource: { buffer: outputBuffer } }
        ];

        if (uniforms) {
            const uniformBuffer = this.createStorageBuffer(uniforms, 'uniforms');
            if (uniformBuffer) {
                entries.push({ binding: 2, resource: { buffer: uniformBuffer } });
            }
        }

        const bindGroup = this.device.createBindGroup({
            layout: (pipeline as any).getBindGroupLayout(0),
            entries
        });

        // Dispatch
        const workgroupCount = Math.ceil(inputData.length / 256);
        const result = await this.dispatch(
            pipeline,
            bindGroup,
            [workgroupCount],
            outputBuffer,
            outputSize * 4
        );

        // Cleanup
        inputBuffer.destroy();
        outputBuffer.destroy();

        return result;
    }

    // ============================================================================
    // TEXTURE OPERATIONS
    // ============================================================================

    async copyTextureToBuffer(
        texture: any, // GPUTexture
        buffer: GPUBuffer,
        width: number,
        height: number
    ): Promise<void> {
        if (!this.device || !this.queue) return;

        const commandEncoder = this.device.createCommandEncoder();
        (commandEncoder as any).copyTextureToBuffer(
            { texture },
            { buffer, bytesPerRow: width * 4 },
            { width, height, depthOrArrayLayers: 1 }
        );
        this.queue.submit([commandEncoder.finish()]);
    }

    // ============================================================================
    // DEPTH READBACK (for Occlusion)
    // ============================================================================

    async readDepthTexture(
        depthTexture: any, // GPUTexture
        x: number,
        y: number,
        width: number = 1,
        height: number = 1
    ): Promise<Float32Array | null> {
        if (!this.device || !this.queue) return null;

        const bytesPerRow = Math.ceil(width * 4 / 256) * 256; // Align to 256
        const bufferSize = bytesPerRow * height;

        const readbackBuffer = this.createReadbackBuffer(bufferSize, 'depth-readback');
        if (!readbackBuffer) return null;

        const commandEncoder = this.device.createCommandEncoder();
        (commandEncoder as any).copyTextureToBuffer(
            { texture: depthTexture, origin: { x, y, z: 0 } },
            { buffer: readbackBuffer, bytesPerRow },
            { width, height, depthOrArrayLayers: 1 }
        );
        this.queue.submit([commandEncoder.finish()]);

        await readbackBuffer.mapAsync(GPUMapMode.READ);
        const data = new Float32Array(readbackBuffer.getMappedRange().slice(0));
        readbackBuffer.unmap();
        readbackBuffer.destroy();

        return data;
    }

    // ============================================================================
    // UTILITY
    // ============================================================================

    get available(): boolean {
        return this.isAvailable;
    }

    get gpuDevice(): GPUDevice | null {
        return this.device;
    }

    getStats() {
        return {
            ...this.stats,
            avgGPUTime: this.stats.computeDispatches > 0
                ? this.stats.totalGPUTime / this.stats.computeDispatches
                : 0
        };
    }

    dispose(): void {
        // Clear pools
        for (const pool of this.bufferPool.values()) {
            for (const buffer of pool) {
                buffer.destroy();
            }
        }
        this.bufferPool.clear();
        this.pipelineCache.clear();
        this.shaderModuleCache.clear();

        // Device will be garbage collected
        this.device = null;
        this.adapter = null;
        this.isAvailable = false;
    }
}

// ============================================================================
// WGSL SHADER LIBRARY
// ============================================================================

// Ray-Triangle Intersection helper (reusable)
const RAY_TRIANGLE_INTERSECT_WGSL = `
    struct RayHit {
        valid: bool,
        distance: f32,
        u: f32,
        v: f32
    }

    fn rayTriangleIntersect(
        origin: vec3f,
        direction: vec3f,
        v0: vec3f,
        v1: vec3f,
        v2: vec3f
    ) -> RayHit {
        let edge1 = v1 - v0;
        let edge2 = v2 - v0;
        let h = cross(direction, edge2);
        let a = dot(edge1, h);

        if (abs(a) < 0.00001) {
            return RayHit(false, 0.0, 0.0, 0.0);
        }

        let f = 1.0 / a;
        let s = origin - v0;
        let u = f * dot(s, h);

        if (u < 0.0 || u > 1.0) {
            return RayHit(false, 0.0, 0.0, 0.0);
        }

        let q = cross(s, edge1);
        let v = f * dot(direction, q);

        if (v < 0.0 || u + v > 1.0) {
            return RayHit(false, 0.0, 0.0, 0.0);
        }

        let t = f * dot(edge2, q);

        if (t > 0.00001) {
            return RayHit(true, t, u, v);
        }

        return RayHit(false, 0.0, 0.0, 0.0);
    }
`;

export const WGSL_SHADERS = {
    RAY_TRIANGLE_INTERSECT: RAY_TRIANGLE_INTERSECT_WGSL,

    // Wall Thickness Compute
    WALL_THICKNESS: `
        @group(0) @binding(0) var<storage, read> positions: array<vec3f>;
        @group(0) @binding(1) var<storage, read> normals: array<vec3f>;
        @group(0) @binding(2) var<storage, read> indices: array<u32>;
        @group(0) @binding(3) var<storage, read_write> thickness: array<f32>;
        @group(0) @binding(4) var<uniform> uniforms: Uniforms;

        struct Uniforms {
            vertexCount: u32,
            triangleCount: u32,
            rayOffset: f32,
            maxDistance: f32
        }

        ${RAY_TRIANGLE_INTERSECT_WGSL}

        @compute @workgroup_size(256)
        fn main(@builtin(global_invocation_id) gid: vec3u) {
            let idx = gid.x;
            if (idx >= uniforms.vertexCount) { return; }

            let origin = positions[idx] + normals[idx] * uniforms.rayOffset;
            let direction = -normals[idx];
            var minDist = uniforms.maxDistance;

            for (var t = 0u; t < uniforms.triangleCount; t++) {
                let i0 = indices[t * 3];
                let i1 = indices[t * 3 + 1];
                let i2 = indices[t * 3 + 2];

                let v0 = positions[i0];
                let v1 = positions[i1];
                let v2 = positions[i2];

                let hit = rayTriangleIntersect(origin, direction, v0, v1, v2);
                if (hit.valid && hit.distance < minDist && hit.distance > 0.001) {
                    minDist = hit.distance;
                }
            }

            thickness[idx] = minDist;
        }
    `,

    // Depth Readback Shader (for occlusion)
    DEPTH_SAMPLE: `
        @group(0) @binding(0) var depthTexture: texture_depth_2d;
        @group(0) @binding(1) var<storage, read_write> output: array<f32>;
        @group(0) @binding(2) var<uniform> samplePos: vec2u;

        @compute @workgroup_size(1)
        fn main() {
            let depth = textureLoad(depthTexture, samplePos, 0);
            output[0] = depth;
        }
    `
};

// Export singleton for easy access
export const fikoWebGPU = FikoWebGPU.getInstance();
